import axios from 'axios';

import { API_BASE_URL } from '../api';
import { auth } from '../firebase/firebase';

export type GitReviewerRunStatus = 'queued' | 'running' | 'completed' | 'failed';
export type GitReviewerVerdict = 'approve' | 'request_changes' | 'comment';
export type GitReviewerFindingSeverity = 'critical' | 'major' | 'minor' | 'info' | 'warning' | 'error';

export interface GitReviewerFinding {
  file: string;
  line?: number;
  /** Older run payloads used `message`; current backend emits `note`. */
  message?: string;
  note?: string;
  severity?: GitReviewerFindingSeverity | string;
}

export interface GitReviewerLogEntry {
  ts: number | string;
  message: string;
  isError?: boolean;
}

export interface GitReviewerRun {
  /** Normalized from the backend's `_id` when necessary. */
  id: string;
  _id?: string;
  repoOwner: string;
  repoName: string;
  prUrl: string;
  prNumber: number;
  prTitle: string;
  status: GitReviewerRunStatus;
  verdict?: GitReviewerVerdict;
  /** Legacy run payloads used `comment`; current backend emits `commentBody`. */
  comment?: string;
  commentBody?: string;
  resultSummary?: string;
  findings?: GitReviewerFinding[];
  logs?: GitReviewerLogEntry[];
  /** Legacy field name; current backend emits `headSha`/`lastReviewedCommitSha`. */
  commitSha?: string;
  headSha?: string;
  lastReviewedCommitSha?: string;
  headBranch?: string;
  selfPr?: boolean;
  githubAction?: string;
  createdAt: string | number;
  updatedAt: string | number;
  completedAt?: string | number;
  error?: string;
}

type GitReviewerRunPayload = Partial<GitReviewerRun> & { id?: string; _id?: string };

/**
 * The backend run list returns `_id` (Mongo), while the FE and the socket progress
 * events have historically used `id`. Without normalization every run has an
 * `undefined` id, which makes the "which run is expanded?" check
 * (`expandedRunId === run.id`) true for every row at once — the shared-accordion bug.
 */
export const normalizeGitReviewerRun = (run: GitReviewerRunPayload): GitReviewerRun => {
  const base = run as GitReviewerRun;

  return {
    ...base,
    id: run.id || run._id || '',
    repoOwner: base.repoOwner || '',
    repoName: base.repoName || '',
    prUrl: base.prUrl || '',
    prNumber: base.prNumber ?? 0,
    prTitle: base.prTitle || '',
    status: base.status || 'queued',
    createdAt: base.createdAt ?? '',
    updatedAt: base.updatedAt ?? '',
  };
};

export type GitReviewerManualRunRequest = { owner: string; repo: string; prNumber: number } | { all: true };

// MF-67/MF-68 contract. `reason` is a human-readable backend skip reason (e.g. "Already reviewed
// at <sha>", identity lacks read access, missing head SHA, already queued/running).
export interface GitReviewerManualRunSkipped {
  owner?: string;
  repo?: string;
  prNumber?: number;
  reason: string;
}

export interface GitReviewerManualRunResult {
  dispatched: GitReviewerRun[];
  skipped: GitReviewerManualRunSkipped[];
}

interface GitReviewerRunsResponse {
  runs?: GitReviewerRunPayload[];
}

interface GitReviewerManualRunResponse {
  dispatched?: GitReviewerRunPayload[];
  skipped?: GitReviewerManualRunSkipped[];
}

export const fetchGitReviewerRuns = async (): Promise<{ runs: GitReviewerRun[] }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<GitReviewerRunsResponse>(`${API_BASE_URL}/api/git-reviewer/runs`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return { runs: (response.data?.runs ?? []).map(normalizeGitReviewerRun) };
};

export const fetchGitReviewerRun = async (id: string): Promise<GitReviewerRun> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<GitReviewerRunPayload>(`${API_BASE_URL}/api/git-reviewer/runs/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizeGitReviewerRun(response.data);
};

// MF-67 — manual on-demand review run. Single PR: `{ owner, repo, prNumber }`; all eligible: `{ all: true }`.
export const triggerGitReviewerRun = async (
  request: GitReviewerManualRunRequest,
): Promise<GitReviewerManualRunResult> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post<GitReviewerManualRunResponse>(`${API_BASE_URL}/api/git-reviewer/run`, request, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return {
    dispatched: (response.data?.dispatched ?? []).map(normalizeGitReviewerRun),
    skipped: response.data?.skipped ?? [],
  };
};

export const gitReviewerErrorMessage = (err: unknown): string | undefined =>
  axios.isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;
